'use client'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useRouter } from '@/lib/next-shim'
import { useEffect, useMemo } from 'react'
import { useSession } from '@/shared/ui/providers/AuthSessionProvider'

import { useBooking } from '../../model/bookingContext'
import { BookingStepEnum } from '../../model/bookingTypes'
import { AuthenticationGate } from '../AuthenticationGate'
import { BookingStep } from './BookingStep'
import { Summary } from './Summary'
import { IncompletedStepLabel } from './IncompletedStepLabel'
import { ExpertiseInfoForm } from './ExpertiseInfoForm'
import { useOrderManagement } from '../../model/useOrderManagement'

export function ExpertiseInfoStep() {
  const { goToStep, isStepCompleted, state } = useBooking()
  const isCompleted = isStepCompleted(BookingStepEnum.EXPERTISE_INFORMATION)

  if (state.currentStep === BookingStepEnum.EXPERTISE_INFORMATION) {
    return (
      <BookingStep className="sm:!rounded-br-none">
        <BookingStep.Title>
          <Trans>Medical Information</Trans>
        </BookingStep.Title>
        <BookingStep.Content>
          <ExpertiseInfo meetingLink={state.meetingLinkSlug} />
        </BookingStep.Content>
      </BookingStep>
    )
  }

  if (isCompleted) {
    return (
      <Summary icon="📋" label={t`Medical Information`} onEdit={() => goToStep(BookingStepEnum.EXPERTISE_INFORMATION)} text={state.expertiseData.comment} />
    )
  }

  return (
    <IncompletedStepLabel icon="📋">
      <Trans>Medical Information</Trans>
    </IncompletedStepLabel>
  )
}

function ExpertiseInfo({ meetingLink }: { meetingLink: string }) {
  const router = useRouter()
  const { dispatch } = useBooking()
  const { status } = useSession()

  useEffect(() => {
    if (typeof window !== 'undefined' && status === 'authenticated') {
      const recoveredOrderId = sessionStorage.getItem('recoveredOrderId')
      if (recoveredOrderId) {
        console.log('Found recovered order ID:', recoveredOrderId)

        // Store the order ID in context
        dispatch({ type: 'SET_REQUIRES_PAYMENT', payload: true })
        dispatch({ type: 'SET_ORDER_ID', payload: recoveredOrderId, goToNextStep: true })

        // Clear the recovery data
        sessionStorage.removeItem('recoveredOrderId')
      }
    }
  }, [status, dispatch, meetingLink, router])

  return (
    <AuthenticationGate returnPath={`/meeting-links/${meetingLink}/booking`}>
      <ProtectedExpertiseInfo meetingLink={meetingLink} />
    </AuthenticationGate>
  )
}

function ProtectedExpertiseInfo({ meetingLink }: { meetingLink: string }) {
  const { dispatch, state } = useBooking()

  const { draftOrder, isDraftingOrder } = useOrderManagement(meetingLink)

  const patientInfo = useMemo(() => {
    const { email, firstName, lastName } = state.patientInfo
    if (!email || !firstName || !lastName) return null
    return state.patientInfo
  }, [state.patientInfo])

  useEffect(() => {
    if (state.orderId || !patientInfo) return

    draftOrder(patientInfo)
  }, [dispatch, draftOrder, patientInfo])

  if (isDraftingOrder) {
    return (
      <div className="flex flex-col gap-4 animate-pulse px-4 py-6 sm:p-0">
        <div className="h-4 rounded bg-slate-200 w-1/2" />
        <div className="h-28 rounded bg-slate-200" />
      </div>
    )
  }

  if (!state.orderId) return null

  return (
    <div className="mt-6">
      <ExpertiseInfoForm orderId={state.orderId} />
    </div>
  )
}
